import React from 'react';
import { Chart } from 'chart.js';

import {
   formatChartNumber,
   getChartDevicePixelRatio,
   MAIN_ANALYTICS_CHART_FONT_SIZE,
   MAIN_ANALYTICS_CHART_COLORS,
} from './chart.constants';
import { renderChartTooltip } from './chart-tooltip';
import { registerMainAnalyticsCharts } from './register-main-analytics-charts';

type DoughnutChartProps = {
   label: string;
   labels: string[];
   values: number[];
   colors?: string[];
};

export const DoughnutChart = ({
   label,
   labels,
   values,
   colors = MAIN_ANALYTICS_CHART_COLORS,
}: DoughnutChartProps) => {
   const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
   const chartRef = React.useRef<Chart<'doughnut', number[], string> | null>(null);

   React.useEffect(() => {
      registerMainAnalyticsCharts();

      const canvas = canvasRef.current;
      if (!canvas) {
         return undefined;
      }

      const chart = new Chart(canvas, {
         type: 'doughnut',
         data: {
            labels: [],
            datasets: [
               {
                  data: [],
                  backgroundColor: [],
                  borderColor: '#ffffff',
                  borderWidth: 2,
               },
            ],
         },
         options: {
            cutout: '62%',
            devicePixelRatio: getChartDevicePixelRatio(),
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
               legend: {
                  position: 'right',
                  labels: {
                     boxWidth: 10,
                     boxHeight: 10,
                     usePointStyle: true,
                     color: '#202020',
                     font: {
                        size: MAIN_ANALYTICS_CHART_FONT_SIZE,
                        weight: 500,
                     },
                  },
               },
               tooltip: {
                  enabled: false,
                  external: renderChartTooltip,
                  callbacks: {
                     label: (item) =>
                        `${item.label}: ${formatChartNumber(Number(item.raw ?? 0))}`,
                  },
               },
            },
         },
      });

      chartRef.current = chart;

      return () => {
         chart.destroy();
         chartRef.current = null;
      };
   }, []);

   React.useEffect(() => {
      const chart = chartRef.current;
      if (!chart) {
         return;
      }

      const dataset = chart.data.datasets[0];
      chart.data.labels = labels;
      dataset.data = values;
      dataset.backgroundColor = labels.map((_, index) => colors[index % colors.length]);
      chart.update();
   }, [colors, labels, values]);

   return <canvas ref={canvasRef} role="img" aria-label={label} />;
};
